import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { paymentApi } from '../lib/api';
import {
  Zap, Crown, Rocket, Star, CheckCircle, ArrowRight,
  Calendar, MapPin, Search, Sparkles, Building2, Percent,
  CreditCard, Loader2, AlertCircle
} from 'lucide-react';

// PayPal icon component
const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.641.641 0 0 1 .632-.544h6.964c2.327 0 4.14.629 5.392 1.872.596.589 1.017 1.274 1.255 2.04.254.815.313 1.749.177 2.776-.019.132-.041.265-.067.4-.452 2.433-1.517 4.246-3.177 5.396-1.562 1.084-3.558 1.634-5.932 1.634h-1.2a.641.641 0 0 0-.633.544l-.848 5.499zm6.112-15.917H8.676l-1.18 7.645h2.513c1.79 0 3.247-.359 4.335-1.068 1.144-.747 1.845-1.867 2.082-3.331.24-1.553-.107-2.666-.892-3.347-.682-.592-1.787-.899-3.346-.899z"/>
  </svg>
);

// ============ BOOKEE (TALENT) SUBSCRIPTION TIERS ============
// These are for talents who want to boost their profile visibility

const BOOKEE_TIER_BENEFITS = {
  bookee_tier1: [
    { icon: Search, text: 'Priority in search results' },
    { icon: Sparkles, text: 'Featured in Explore section' },
    { icon: Star, text: 'Highlighted profile badge' },
  ],
  bookee_tier2: [
    { icon: Search, text: 'Priority in search results' },
    { icon: Sparkles, text: 'Featured in Explore section' },
    { icon: Star, text: 'Highlighted profile badge' },
    { icon: Calendar, text: 'Public calendar spotlight' },
    { icon: Zap, text: 'Talent recommendation for searches' },
  ],
  bookee_tier3: [
    { icon: Search, text: 'Top priority in search results' },
    { icon: Sparkles, text: 'Premium Explore placement' },
    { icon: Crown, text: 'VIP profile badge' },
    { icon: Calendar, text: 'Public calendar spotlight' },
    { icon: Zap, text: 'AI talent recommendation priority' },
    { icon: Building2, text: 'Discounts at partner venues' },
    { icon: Percent, text: 'Reduced platform fees' },
  ],
};

// Yearly discount percentages by tier
const YEARLY_DISCOUNTS = {
  tier1: 0,      // No discount for tier 1 yearly
  tier2: 0.15,   // 15% discount for tier 2 yearly
  tier3: 0.20,   // 20% discount for tier 3 yearly
};

const BOOKEE_SUBSCRIPTION_TIERS = [
  {
    id: 'bookee_tier1',
    name: 'Starter Boost',
    subtitle: 'Get Discovered',
    description: 'Get discovered by more bookers',
    monthlyPrice: 9.99,
    period: '/month',
    yearlyDiscount: YEARLY_DISCOUNTS.tier1,
    icon: Zap,
    color: 'from-blue-500 to-cyan-500',
    borderColor: 'border-blue-500/30',
    bgColor: 'bg-blue-500/10',
    popular: false,
  },
  {
    id: 'bookee_tier2',
    name: 'Pro Boost',
    subtitle: 'Maximize Visibility',
    description: 'Stand out and get more bookings',
    monthlyPrice: 19.99,
    period: '/month',
    yearlyDiscount: YEARLY_DISCOUNTS.tier2,
    icon: Rocket,
    color: 'from-violet-500 to-purple-500',
    borderColor: 'border-violet-500/30',
    bgColor: 'bg-violet-500/10',
    popular: true,
  },
  {
    id: 'bookee_tier3',
    name: 'VIP Boost',
    subtitle: 'Elite Status',
    description: 'The ultimate profile boost package',
    monthlyPrice: 49.99,
    period: '/month',
    yearlyDiscount: YEARLY_DISCOUNTS.tier3,
    icon: Crown,
    color: 'from-amber-500 to-orange-500',
    borderColor: 'border-amber-500/30',
    bgColor: 'bg-amber-500/10',
    popular: false,
  },
];

// ============ BOOKER (CLIENT) SUBSCRIPTION TIERS ============
// These are for clients who want premium booking features

const BOOKER_TIER_BENEFITS = {
  booker_tier1: [
    { icon: Percent, text: 'Reduced service fees' },
    { icon: Star, text: 'Priority customer support' },
    { icon: Zap, text: 'Early access to new bookees' },
  ],
  booker_tier2: [
    { icon: Percent, text: 'Lower service fees' },
    { icon: Star, text: 'Priority customer support' },
    { icon: Zap, text: 'Early access to new bookees' },
    { icon: Calendar, text: 'Priority booking slots' },
    { icon: Search, text: 'Advanced search filters' },
  ],
  booker_tier3: [
    { icon: Percent, text: 'Lowest service fees' },
    { icon: Crown, text: 'VIP customer support' },
    { icon: Zap, text: 'Exclusive bookee access' },
    { icon: Calendar, text: 'Priority booking slots' },
    { icon: Search, text: 'Advanced search filters' },
    { icon: Building2, text: 'Partner venue discounts' },
    { icon: Sparkles, text: 'Concierge booking service' },
  ],
};

const BOOKER_SUBSCRIPTION_TIERS = [
  {
    id: 'booker_tier1',
    name: 'Basic',
    subtitle: 'Smart Saver',
    description: 'Save on every booking',
    monthlyPrice: 4.99,
    period: '/month',
    yearlyDiscount: YEARLY_DISCOUNTS.tier1,
    icon: Zap,
    color: 'from-emerald-500 to-teal-500',
    borderColor: 'border-emerald-500/30',
    bgColor: 'bg-emerald-500/10',
    popular: false,
  },
  {
    id: 'booker_tier2',
    name: 'Premium',
    subtitle: 'Power Booker',
    description: 'More features, more savings',
    monthlyPrice: 9.99,
    period: '/month',
    yearlyDiscount: YEARLY_DISCOUNTS.tier2,
    icon: Rocket,
    color: 'from-blue-500 to-indigo-500',
    borderColor: 'border-blue-500/30',
    bgColor: 'bg-blue-500/10',
    popular: true,
  },
  {
    id: 'booker_tier3',
    name: 'VIP',
    subtitle: 'Elite Booker',
    description: 'The ultimate booking experience',
    monthlyPrice: 19.99,
    period: '/month',
    yearlyDiscount: YEARLY_DISCOUNTS.tier3,
    icon: Crown,
    color: 'from-purple-500 to-pink-500',
    borderColor: 'border-purple-500/30',
    bgColor: 'bg-purple-500/10',
    popular: false,
  },
];

const Subscriptions = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [selectedTier, setSelectedTier] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('stripe');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'

  // Determine which tiers and benefits to show based on user type
  const isBookee = user?.user_type === 'talent' || user?.user_type === 'both';
  const SUBSCRIPTION_TIERS = isBookee ? BOOKEE_SUBSCRIPTION_TIERS : BOOKER_SUBSCRIPTION_TIERS;
  const TIER_BENEFITS = isBookee ? BOOKEE_TIER_BENEFITS : BOOKER_TIER_BENEFITS;

  // Calculate price based on billing cycle
  const getPrice = (tier) => {
    if (!tier.monthlyPrice) return null;
    if (billingCycle === 'yearly') {
      const yearlyTotal = tier.monthlyPrice * 12;
      const discount = tier.yearlyDiscount || 0;
      const discountedTotal = yearlyTotal * (1 - discount);
      return discountedTotal;
    }
    return tier.monthlyPrice;
  };

  // Get monthly equivalent for yearly plans (for display)
  const getMonthlyEquivalent = (tier) => {
    if (billingCycle === 'yearly' && tier.monthlyPrice) {
      const yearlyPrice = getPrice(tier);
      return yearlyPrice / 12;
    }
    return tier.monthlyPrice;
  };

  useEffect(() => {
    loadPaymentMethods();
    if (isAuthenticated) {
      loadCurrentSubscription();
    }
  }, [isAuthenticated]);

  const loadPaymentMethods = async () => {
    try {
      const response = await paymentApi.getPaymentMethods();
      setPaymentMethods(response.data.payment_methods || []);
      if (response.data.payment_methods?.length > 0) {
        setSelectedPaymentMethod(response.data.payment_methods[0].id);
      }
    } catch (error) {
      console.error('Failed to load payment methods:', error);
    }
  };

  const loadCurrentSubscription = async () => {
    // Check user's current subscription status
    if (user?.subscription_tier) {
      setCurrentSubscription(user.subscription_tier);
    }
  };

  const handleSelectTier = (tier) => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/subscriptions');
      return;
    }

    if (!tier.monthlyPrice) {
      toast.info('This tier is coming soon! Pricing will be announced shortly.');
      return;
    }
    
    setSelectedTier(tier);
    setPaymentDialogOpen(true);
  };

  const handleSubscribe = async () => {
    if (!selectedTier) return;
    
    setPaymentLoading(true);
    try {
      const price = getPrice(selectedTier);
      const subscriptionData = {
        tier_id: selectedTier.id,
        tier_name: selectedTier.name,
        amount: price,
        billing_cycle: billingCycle,
        payment_method: selectedPaymentMethod
      };

      if (selectedPaymentMethod === 'paypal') {
        // Create PayPal subscription order
        const response = await paymentApi.createSubscriptionPayPal(subscriptionData);
        if (response.data.approval_url) {
          window.location.href = response.data.approval_url;
        } else {
          throw new Error('No PayPal approval URL received');
        }
      } else {
        // Create Stripe subscription checkout
        const response = await paymentApi.createSubscriptionStripe(subscriptionData);
        if (response.data.checkout_url || response.data.url) {
          window.location.href = response.data.checkout_url || response.data.url;
        } else {
          throw new Error('No Stripe checkout URL received');
        }
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start subscription. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'TBD';
    return `$${price.toFixed(2)}`;
  };

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="subscriptions-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${isBookee ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400' : 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-400'} mb-4`}>
            <Zap className="h-4 w-4" />
            <span className="text-sm font-medium">{isBookee ? 'Boost Your Profile' : 'Premium Membership'}</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
            <span className={`bg-gradient-to-r ${isBookee ? 'from-amber-400 via-orange-400 to-rose-400' : 'from-emerald-400 via-teal-400 to-cyan-400'} bg-clip-text text-transparent`}>
              {isBookee ? 'Get More Bookings' : 'Save More, Book Better'}
            </span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {isBookee 
              ? 'Stand out from the crowd and get discovered by more bookers with our profile boost subscriptions'
              : 'Unlock exclusive benefits, save on fees, and get priority access to top talent'}
          </p>

          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-primary text-white'
                  : 'bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              data-testid="billing-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all relative ${
                billingCycle === 'yearly'
                  ? 'bg-primary text-white'
                  : 'bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              data-testid="billing-yearly"
            >
              Yearly
              <span className="absolute -top-3 -right-2 px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full">
                SAVE UP TO 20%
              </span>
            </button>
          </div>
        </div>

        {/* Current Subscription Status */}
        {currentSubscription && (
          <Card className="mb-8 border-green-500/30 bg-green-500/10">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  <div>
                    <p className="font-medium">Active Subscription</p>
                    <p className="text-sm text-muted-foreground">
                      You&apos;re currently on {currentSubscription}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-lg">
                  Manage Subscription
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Benefits Highlight - Dynamic based on user type */}
        <div className="grid md:grid-cols-4 gap-4 mb-12">
          {(isBookee ? [
            { icon: Sparkles, text: 'Featured in Explore', desc: 'Appear in the Explore section' },
            { icon: Calendar, text: 'Public Calendar', desc: 'Get featured on public calendar' },
            { icon: Building2, text: 'Venue Discounts', desc: 'Discounts at partner venues' },
            { icon: Search, text: 'Top Search Results', desc: 'Show up in talent recommendations' },
          ] : [
            { icon: Percent, text: 'Reduced Fees', desc: 'Save on every booking' },
            { icon: Star, text: 'Priority Support', desc: 'Get help when you need it' },
            { icon: Calendar, text: 'Priority Booking', desc: 'First access to busy dates' },
            { icon: Crown, text: 'VIP Experience', desc: 'Exclusive perks and benefits' },
          ]).map((benefit, idx) => (
            <Card key={idx} className="text-center border-border/50">
              <CardContent className="pt-6">
                <div className={`w-12 h-12 rounded-full ${isBookee ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20' : 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20'} flex items-center justify-center mx-auto mb-3`}>
                  <benefit.icon className={`h-6 w-6 ${isBookee ? 'text-amber-400' : 'text-emerald-400'}`} />
                </div>
                <p className="font-medium">{benefit.text}</p>
                <p className="text-sm text-muted-foreground mt-1">{benefit.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Subscription Tiers */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {SUBSCRIPTION_TIERS.map((tier) => (
            <Card 
              key={tier.id}
              className={`relative overflow-hidden transition-all duration-300 hover:scale-[1.02] ${tier.borderColor} ${selectedTier?.id === tier.id ? 'ring-2 ring-primary' : ''}`}
              data-testid={`subscription-${tier.id}`}
            >
              {tier.popular && (
                <div className="absolute top-0 right-0">
                  <Badge className="rounded-none rounded-bl-lg bg-gradient-to-r from-violet-500 to-purple-500 text-white border-0">
                    Most Popular
                  </Badge>
                </div>
              )}
              
              {currentSubscription === tier.id && (
                <div className="absolute top-0 left-0">
                  <Badge className="rounded-none rounded-br-lg bg-green-500 text-white border-0">
                    Current Plan
                  </Badge>
                </div>
              )}
              
              <CardHeader className={`${tier.bgColor}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-xl bg-gradient-to-br ${tier.color}`}>
                    <tier.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl" style={{ fontFamily: 'Syne, sans-serif' }}>
                      {tier.name}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">{tier.subtitle}</p>
                  </div>
                </div>
                <CardDescription>{tier.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="pt-6">
                <div className="mb-6">
                  {billingCycle === 'yearly' && tier.yearlyDiscount > 0 && (
                    <div className="mb-2">
                      <span className="text-sm text-muted-foreground line-through">
                        ${(tier.monthlyPrice * 12).toFixed(2)}/year
                      </span>
                      <Badge className="ml-2 bg-green-500 text-white text-xs">
                        Save {Math.round(tier.yearlyDiscount * 100)}%
                      </Badge>
                    </div>
                  )}
                  <span className="text-3xl font-bold">{formatPrice(getPrice(tier))}</span>
                  <span className="text-muted-foreground">
                    {billingCycle === 'yearly' ? '/year' : '/month'}
                  </span>
                  {billingCycle === 'yearly' && (
                    <p className="text-sm text-muted-foreground mt-1">
                      (${getMonthlyEquivalent(tier)?.toFixed(2)}/month)
                    </p>
                  )}
                </div>
                
                <ul className="space-y-3 mb-6">
                  {TIER_BENEFITS[tier.id]?.map((benefit, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                      <span className="text-sm">{benefit.text}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full rounded-xl ${tier.popular ? `bg-gradient-to-r ${tier.color} hover:opacity-90` : ''}`}
                  variant={tier.popular ? 'default' : 'outline'}
                  onClick={() => handleSelectTier(tier)}
                  disabled={currentSubscription === tier.id}
                  data-testid={`select-${tier.id}-btn`}
                >
                  {currentSubscription === tier.id ? (
                    'Current Plan'
                  ) : !tier.monthlyPrice ? (
                    'Coming Soon'
                  ) : (
                    <>
                      Subscribe
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8" style={{ fontFamily: 'Syne, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: 'What happens when I boost my profile?',
                a: 'Your profile gets priority placement in search results, appears in the Explore section, and you may be recommended to bookers searching for talent like you.'
              },
              {
                q: 'Can I cancel my subscription anytime?',
                a: 'Yes, you can cancel your subscription at any time. Your boost benefits will remain active until the end of your billing period.'
              },
              {
                q: 'How do venue discounts work?',
                a: 'Tier 3 subscribers get exclusive discounts at our partner venues when making self-bookings for events, rehearsals, or studio time.'
              },
              {
                q: 'What payment methods are accepted?',
                a: 'We accept credit/debit cards via Stripe and PayPal for subscription payments.'
              },
            ].map((faq, idx) => (
              <Card key={idx}>
                <CardContent className="pt-4">
                  <h3 className="font-medium mb-2">{faq.q}</h3>
                  <p className="text-sm text-muted-foreground">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Subscribe to {selectedTier?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTier && (
            <div className="space-y-6 py-4">
              {/* Plan Summary */}
              <Card className={`${selectedTier.bgColor} ${selectedTier.borderColor}`}>
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl bg-gradient-to-br ${selectedTier.color}`}>
                      <selectedTier.icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{selectedTier.name} - {selectedTier.subtitle}</p>
                      <p className="text-sm text-muted-foreground">{selectedTier.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold">{formatPrice(getPrice(selectedTier))}</p>
                      <p className="text-xs text-muted-foreground">
                        {billingCycle === 'yearly' ? 'per year' : 'per month'}
                      </p>
                      {billingCycle === 'yearly' && selectedTier.yearlyDiscount > 0 && (
                        <Badge className="mt-1 bg-green-500 text-white text-xs">
                          {Math.round(selectedTier.yearlyDiscount * 100)}% off
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Separator />

              {/* Payment Method Selection */}
              <div className="space-y-3">
                <p className="text-sm font-medium">Select Payment Method:</p>
                <div className="grid grid-cols-2 gap-3">
                  {paymentMethods.map(method => (
                    <Button
                      key={method.id}
                      variant={selectedPaymentMethod === method.id ? "default" : "outline"}
                      className={`rounded-xl h-14 flex flex-col items-center justify-center gap-1 ${
                        selectedPaymentMethod === method.id ? 'ring-2 ring-primary' : ''
                      }`}
                      onClick={() => setSelectedPaymentMethod(method.id)}
                      disabled={!method.available}
                      data-testid={`subscription-payment-${method.id}`}
                    >
                      {method.id === 'stripe' ? (
                        <CreditCard className="h-5 w-5" />
                      ) : (
                        <PayPalIcon />
                      )}
                      <span className="text-xs">{method.name}</span>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Info */}
              <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p>Your subscription will automatically renew {billingCycle === 'yearly' ? 'yearly' : 'monthly'}. You can cancel anytime from your account settings.</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setPaymentDialogOpen(false)}
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubscribe}
                  disabled={paymentLoading || !selectedPaymentMethod}
                  className={`flex-1 rounded-xl bg-gradient-to-r ${selectedTier.color} hover:opacity-90`}
                  data-testid="confirm-subscription-btn"
                >
                  {paymentLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : selectedPaymentMethod === 'paypal' ? (
                    <PayPalIcon />
                  ) : (
                    <CreditCard className="h-4 w-4 mr-2" />
                  )}
                  {paymentLoading ? 'Processing...' : `Pay ${formatPrice(getPrice(selectedTier))}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Subscriptions;
